import React, { useEffect, useRef } from 'react';
import {
  Box,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import { HorizontalNavBar } from '../navigation/HorizontalNavBar';
import { SecondaryNavBar } from '../navigation/SecondaryNavBar';

interface MainLayoutProps {
  children: React.ReactNode;
}

/**
 * Session keep-alive: refresh the auth cookie every 30 minutes
 * so the user never gets a silent 403 during a trading session.
 */
const SESSION_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const refreshSession = async () => {
      // auth-service expects the refresh token in the JSON body (not a cookie);
      // posting {} just 422-spammed on every mount. Skip when we have no token.
      const refreshToken = localStorage.getItem('qiyas_refresh_token');
      if (!refreshToken) return;
      try {
        const r = await axios.post(
          '/api/v1/auth/refresh',
          { refresh_token: refreshToken },
          { withCredentials: true },
        );
        if (r.data?.access_token) {
          localStorage.setItem('qiyas_access_token', r.data.access_token);
          // Mirror for the chief orchestrator console (@chief reads 'access_token')
          localStorage.setItem('access_token', r.data.access_token);
        }
        if (r.data?.refresh_token) localStorage.setItem('qiyas_refresh_token', r.data.refresh_token);
      } catch {
        // If refresh fails, session is truly expired — redirect handled by interceptors
      }
    };

    // Refresh immediately on mount, then every 30 minutes
    refreshSession();
    refreshTimer.current = setInterval(refreshSession, SESSION_REFRESH_INTERVAL);

    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        overflowX: 'hidden',
        // Hide scrollbars globally for Tier-1 clean look
        '& *::-webkit-scrollbar': { width: 6, height: 6 },
        '& *::-webkit-scrollbar-track': { background: 'transparent' },
        '& *::-webkit-scrollbar-thumb': { background: (theme) => theme.palette.action.disabled, borderRadius: 3 },
        '& *::-webkit-scrollbar-thumb:hover': { background: '#999' },
      }}
    >
      {/* Primary Navigation Bar */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
        }}
      >
        <HorizontalNavBar />
        <SecondaryNavBar />
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: 0,
          px: 0,
          pb: 0,
          width: '100%',
          overflowX: 'hidden',
          // Account for both nav bars: primary (48px) + secondary (40px) = 88px
          marginTop: '88px',
          // Hide scrollbars for Tier-1 clean look
          '&::-webkit-scrollbar': { width: 0, height: 0 },
          scrollbarWidth: 'none',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
